<template>
  <div>
    <LoadingComponent :loading="loading" :message="$t('system.messages.loading')" show-elapsed style="z-index: 1000;"></LoadingComponent>
    <v-dialog v-model="validacaoDialog" scrollable max-width="1100" style="border-radius: 10px;">
      <v-card>
        <v-card-title style="color: #4a4a4a">
          {{ $t('externalAddress.validationResult.title') }}
          <v-spacer></v-spacer>
          <v-chip small label :color="validacaoTemErro ? 'red darken-2' : 'green darken-2'" dark>
            {{ validacaoTemErro
              ? $t('externalAddress.validationResult.withErrors')
              : $t('externalAddress.validationResult.allOk') }}
          </v-chip>
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 70vh;">
          <div
            v-for="(res, idx) in validacaoResultados"
            :key="'res-' + res.id + '-' + idx"
            class="mt-4">
            <div class="d-flex align-center flex-wrap">
              <strong style="color: #4a4a4a">{{ res.nome }}</strong>
              <span v-if="res.enderecoResumo" class="text-caption grey--text text--darken-1 ml-2">
                {{ res.enderecoResumo }}
              </span>
              <v-spacer></v-spacer>
              <v-chip x-small label class="ml-2" :color="res.erro || res.rejeitado ? 'red darken-2' : 'green darken-2'" dark>
                {{ res.erro
                  ? $t('externalAddress.validationResult.failed')
                  : res.rejeitado
                    ? $t('externalAddress.validationResult.rejected')
                    : $t('externalAddress.validationResult.succeeded') }}
              </v-chip>
              <v-chip v-if="res.situacaoLabel" x-small label outlined class="ml-2">{{ res.situacaoLabel }}</v-chip>
            </div>

            <p class="mb-2 mt-1" :class="res.erro ? 'red--text text--darken-2' : ''">{{ res.mensagem }}</p>

            <v-simple-table v-if="res.cercas.length" dense class="elevation-1" style="border-radius: 8px;">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>{{ $t('candidate.jobOffer.shift') }}</th>
                    <th>{{ $t('candidate.headers.main.bestLine') }}</th>
                    <th>{{ $t('candidate.headers.main.bestStop') }}</th>
                    <th class="text-right">{{ $t('externalAddress.validationResult.walkDistance') }}</th>
                    <th class="text-right">{{ $t('candidate.rideTime.label') }}</th>
                    <th class="text-right">{{ $t('externalAddress.validationResult.availableSeats') }}</th>
                    <th>{{ $t('externalAddress.validationResult.status') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(cerca, i) in res.cercas" :key="'cerca-' + idx + '-' + i">
                    <td>{{ cerca.turno }}</td>
                    <td>{{ cerca.linha }}</td>
                    <td>{{ cerca.cerca }}</td>
                    <td class="text-right">
                      {{ cerca.distancia }}
                      <v-tooltip top v-if="!cerca.distanciaReal">
                        <template v-slot:activator="{ on }">
                          <span v-on="on" class="grey--text">!</span>
                        </template>
                        <div>{{ $t('externalAddress.validationResult.straightLineFallback') }}</div>
                      </v-tooltip>
                    </td>
                    <td class="text-right">
                      <span :class="cerca.tempoLinhaExcede ? 'orange--text text--darken-3 font-weight-medium' : ''">
                        {{ cerca.tempoLinha }}
                      </span>
                      <v-tooltip top v-if="cerca.tempoLinhaExcede">
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" x-small color="orange darken-3" class="ml-1">mdi-clock-alert-outline</v-icon>
                        </template>
                        <div>{{ cerca.tempoLinhaTooltip }}</div>
                      </v-tooltip>
                    </td>
                    <td class="text-right">{{ cerca.vagas }}</td>
                    <td>
                      <span class="validacao-status-dot" :style="{ backgroundColor: cerca.cor }"></span>
                      {{ cerca.status }}
                      <v-icon v-if="cerca.precisaAjusteTurno" x-small color="orange darken-2" class="ml-1">
                        mdi-alert
                      </v-icon>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <p v-else class="text-caption grey--text mb-0">
              {{ $t('externalAddress.validationResult.noFences') }}
            </p>

            <v-divider v-if="idx < validacaoResultados.length - 1" class="mt-4" />
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" class="white--text" @click="validacaoDialog = false">{{ $t('button.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" persistent max-width="900" style="border-radius: 10px;">
      <v-card>
        <v-card-title style="color: #4a4a4a">{{ formTitle }}</v-card-title>
        <v-card-text>
          <v-alert
            v-if="editando && editedItem.id_ticket"
            type="info"
            dense
            outlined
            class="mb-4">
            {{ $t('preCandidate.form.fromTicketWarning', { ticket: editedItem.id_ticket }) }}
          </v-alert>

          <v-form ref="formPreCandidato" v-model="formValido">
            <v-row>
              <v-col cols="12" sm="7" md="7">
                <v-text-field
                  v-model="editedItem.nome_completo"
                  :label="$t('preCandidate.headers.name')"
                  :counter="300"
                  maxlength="300"
                  :rules="[(v) => !!v || $t('preCandidate.form.nameRequired')]" />
              </v-col>
              <v-col cols="12" sm="5" md="5">
                <v-select
                  v-model="editedItem.id_agencia_emprego"
                  :items="agencias"
                  :item-text="agenciaLabel"
                  item-value="id_agencia_emprego"
                  :label="$t('candidate.headers.main.agency')"
                  clearable />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="4" md="4">
                <v-text-field
                  v-model="editedItem.cep"
                  :label="$t('address.postalCode')"
                  :loading="loadingCep"
                  maxlength="20"
                  @blur="consultaCEP" />
              </v-col>
              <v-col cols="12" sm="4" md="4">
                <v-text-field
                  v-model="editedItem.turno_desejado"
                  :label="$t('preCandidate.headers.desiredShift')"
                  :hint="$t('preCandidate.form.desiredShiftHint')"
                  persistent-hint
                  maxlength="120" />
              </v-col>
              <v-col cols="12" sm="4" md="4">
                <v-select
                  v-model="editedItem.validacao"
                  :items="situacoes"
                  item-text="nome"
                  item-value="id_situacao_validacao"
                  :label="$tc('situation.self', 1)"
                  clearable />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6" md="6">
                <v-text-field v-model="editedItem.cidade" :loading="loadingCep"
                  :label="$t('address.city')" maxlength="120" />
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <v-text-field v-model="editedItem.bairro" :loading="loadingCep"
                  :label="$t('address.district')" maxlength="120" />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="9" md="9">
                <v-text-field v-model="editedItem.rua" :loading="loadingCep"
                  :label="$t('address.street')" maxlength="300" />
              </v-col>
              <v-col cols="12" sm="3" md="3">
                <v-text-field v-model="editedItem.numero" :label="$t('address.number')" maxlength="30" />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text :disabled="salvando" @click="fecharDialog">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="primary" class="white--text" :loading="salvando" @click="salvarPreCandidato">
            {{ $t('button.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container class="toolbar__container" style="margin-bottom: 0;">
      <v-row align="center">
        <v-col cols="12" sm="4" md="4">
          <v-select
            v-model="filtroAgenciaSelected"
            :items="agenciasFiltro"
            :item-text="agenciaLabel"
            item-value="id_agencia_emprego"
            :label="$t('externalAddress.filters.agency')"
            clearable
            @change="onFiltroChange" />
        </v-col>
        <v-col cols="12" sm="4" md="4">
          <v-select
            v-model="filtroSituacaoSelected"
            :items="situacoes"
            item-text="nome"
            item-value="id_situacao_validacao"
            :label="$t('preCandidate.filters.situation')"
            clearable
            @change="onFiltroChange" />
        </v-col>
        <v-col cols="12" sm="4" md="4">
          <v-switch
            v-model="filtroSomenteAvulsos"
            :label="$t('preCandidate.filters.onlyStandalone')"
            hide-details
            class="mt-0"
            @change="onFiltroChange" />
        </v-col>
      </v-row>
      <v-row v-if="filtroIds" dense>
        <v-col cols="12">
          <v-chip small close color="primary" outlined @click:close="limparFiltroIds">
            <v-icon x-small left>mdi-check-all</v-icon>
            {{ $t('relExternalAddressKpis.selection.fromReport', { count: totalFiltroIds }) }}
          </v-chip>
        </v-col>
      </v-row>
      <v-row v-if="filtroPoligono" dense>
        <v-col cols="12">
          <v-chip small close color="primary" outlined @click:close="$emit('limpar-poligono')">
            <v-icon x-small left>mdi-vector-polygon</v-icon>
            {{ $t('candidate.map.areaFilter') }}
          </v-chip>
        </v-col>
      </v-row>
      <KpisEnderecosOrigemPainel origem="P" :ativa="ativa" :id-agencia="filtroAgenciaSelected" :turnos="turnos" />
    </v-container>

    <merus-data-table
      :headers="headers"
      :items="preCandidatos"
      :loading="loadingtable"
      :loading-text="validacaoLoadingText"
      :show-select="true"
      v-model="preCandidatosSelected"
      item-key="id_pre_candidato"
      :server-items-length="totalPreCandidatos"
      :items-per-page="numberOfRows"
      :items-per-page-last-option="itemsPerPageLastOption"
      :server-sort-by="serverSortBy"
      :server-sort-desc="serverSortDesc"
      :default-multi-sort="defaultSort"
      fixed-header
      @update:options="onTableOptions"
      @update:filters="onFiltersUpdate"
      style="border-radius: 10px; margin-top: 0px; margin-bottom: 20px;">

      <template v-slot:toolbar>
        <CadastroToolbar
          :title="$t('preCandidate.title')"
          :token="token || ''"
          :items="preCandidatos"
          :load-items-for-export="loadItemsForExport"
          background-export
          list-endpoint="getallprecandidatos"
          :export-filters="requestFilters"
          :export-sort-by="serverSortBy"
          :export-sort-desc="serverSortDesc"
          :export-total="totalPreCandidatos"
          id-field="id_pre_candidato"
          :csv-title="$t('preCandidate.title')"
          :csv-headers="headersCsvExport"
          :pdf-title="$t('preCandidate.title')"
          :pdf-headers="headers"
          pdf-filename="pre-candidatos"
          hide-search>
          <template v-slot:add>
            <v-menu offset-y :close-on-content-click="false" min-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-tooltip top>
                  <template v-slot:activator="{ on: onTooltip }">
                    <v-btn color="#4a4a4a" dark small class="mb-2 mr-2" fab v-bind="attrs" v-on="{ ...onTooltip, ...on }">
                      <v-icon>mdi-tune-variant</v-icon>
                    </v-btn>
                  </template>
                  <div>{{ $t('candidate.validationOptions.title') }}</div>
                </v-tooltip>
              </template>
              <v-card>
                <v-card-title class="text-subtitle-2 py-2">
                  {{ $t('candidate.validationOptions.title') }}
                </v-card-title>
                <v-divider />
                <v-card-text class="py-2">
                  <v-switch v-model="validacaoCheckCapacity" dense hide-details class="mt-0"
                    :label="$t('candidate.validationOptions.checkCapacity')" />
                  <v-switch v-model="validacaoConsiderarReservas" dense hide-details class="mt-0"
                    :label="$t('candidate.validationOptions.considerReservations')" />
                  <v-switch v-model="validacaoCalcularCaminhada" dense hide-details class="mt-0"
                    :label="$t('candidate.validationOptions.walkDistance')" />
                  <v-switch v-model="validacaoConsiderarTempoPermanencia" dense hide-details class="mt-0"
                    :label="$t('candidate.validationOptions.considerRideTime')" />
                  <v-text-field v-model="validacaoTempoMaximoPermanencia" type="number" min="1" suffix="min"
                    :disabled="!validacaoConsiderarTempoPermanencia"
                    dense clearable class="mt-3"
                    :label="$t('candidate.validationOptions.maxRideTime')"
                    :hint="$t('candidate.validationOptions.maxRideTimeHint')" persistent-hint />
                </v-card-text>
              </v-card>
            </v-menu>

            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn color="#4a4a4a" dark small class="mb-2 mr-2" fab v-on="on"
                  @click="validaPreCandidatos([...preCandidatosSelected])">
                  <v-icon>mdi-check</v-icon>
                </v-btn>
              </template>
              <div>{{ $t('preCandidate.validateSelected') }}</div>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn color="#4a4a4a" dark small class="mb-2" fab v-on="on" @click="novoPreCandidato">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <div>{{ $t('preCandidate.new') }}</div>
            </v-tooltip>
          </template>
        </CadastroToolbar>
      </template>

      <template v-slot:item.zonas_risco_label="{ item }">
        <ZonaRiscoChip :rotulo="item.zonas_risco_label" />
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon small class="mr-2" color="blue" v-on="on" @click="validaPreCandidatos([item])">mdi-check</v-icon>
          </template>
          <div>{{ $t('preCandidate.validateRow') }}</div>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon small class="mr-2" v-on="on" @click="editarPreCandidato(item)">mdi-pencil</v-icon>
          </template>
          <div>{{ $t('preCandidate.edit') }}</div>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon small class="mr-2" color="green darken-2" v-on="on"
              @click="$emit('progredir', item)">mdi-account-arrow-up</v-icon>
          </template>
          <div>{{ $t('preCandidate.promoteToCandidate') }}</div>
        </v-tooltip>
      </template>
    </merus-data-table>
  </div>
</template>

<script>
import axios from "axios";
import apiController from "@/utils/apiController";
import ZonaRiscoChip from "@/components/Areas/ZonaRiscoChip.vue";
import EventBus from "@/utils/eventBus";
import registrarChamadaApiExterna from "@/utils/registrarChamadaApiExterna";
import LoadingComponent from "../LoadingComponent.vue";
import MerusDataTable from "../Tables/MerusDataTable.vue";
import KpisEnderecosOrigemPainel from "../Enderecos/KpisEnderecosOrigemPainel.vue";
import CadastroToolbar from "../Toolbars/CadastroToolbar.vue";
import serverTableMixin from "@/mixins/serverTableMixin";
import selecaoRevalidacaoMixin from "@/mixins/selecaoRevalidacaoMixin";
import validacaoSchedulerMixin from "@/mixins/validacaoSchedulerMixin";
import {
  buildRequestParams,
  computePageState,
  initSortFromDefault,
  fetchAllServerPagesForExport,
} from "@/utils/serverTablePagination";

/** Sem agência vinculada: valor sentinela do select (o backend traduz -1 para IS NULL). */
const AGENCIA_SEM_VINCULO = -1;

/** Mesmas cores do mapa em CRUDCandidatos, por código `valido` da validação. */
const COR_VALIDACAO = {
  1: "#2E7D32",
  2: "#F57C00",
  3: "#1565C0",
  4: "#C62828",
  other: "#B71C1C",
  none: "#9E9E9E",
};

/**
 * `id_ticket` fica só de leitura: identifica o ticket de onde a linha foi importada.
 * O cadastro pela tela nasce sem ticket (avulso).
 */
const PRE_CANDIDATO_VAZIO = {
  id_pre_candidato: null,
  id_ticket: null,
  id_agencia_emprego: null,
  nome_completo: null,
  cep: null,
  cidade: null,
  bairro: null,
  rua: null,
  numero: null,
  turno_desejado: null,
  validacao: null,
};

export default {
  name: "PreCandidatosTab",
  components: {
    LoadingComponent,
    ZonaRiscoChip,
    MerusDataTable,
    CadastroToolbar,
    KpisEnderecosOrigemPainel,
  },
  mixins: [serverTableMixin, validacaoSchedulerMixin, selecaoRevalidacaoMixin],
  props: {
    /** Aba visível. A tela pai controla a ordem das abas; aqui só interessa estar ativa. */
    ativa: {
      type: Boolean,
      default: false,
    },
    token: {
      type: String,
      default: null,
    },
    /** Agências já carregadas pela tela pai. */
    agencias: {
      type: Array,
      default: () => [],
    },
    /** Polígono desenhado no mapa ("lat,lng;lat,lng;..."): lista só os cadastros dentro dele e os marca. */
    filtroPoligono: {
      type: String,
      default: '',
    },
    situacoes: {
      type: Array,
      default: () => [],
    },
    /** Turnos já carregados pela tela pai (chips de KPI por turno). */
    turnos: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    preCandidatos: [],
    loadingtable: false,
    totalPreCandidatos: 0,
    pageNumber: 1,
    numberOfRows: 10,
    itemsPerPageLastOption: 1000,
    lastItemsPerPageBeforeTodos: 10,
    serverSortBy: [],
    serverSortDesc: [],
    searchFilters: {},
    filtroAgenciaSelected: null,
    filtroSituacaoSelected: null,
    filtroSomenteAvulsos: false,
    carregado: false,
    preCandidatosSelected: [],
    validacaoDialog: false,
    validacaoResultados: [],
    // Capacidade, caminhada e tempo de permanência são opt-in, como no endpoint.
    validacaoCheckCapacity: false,
    validacaoConsiderarReservas: true,
    // Desligado, a validação fica na distância em linha reta (sem o planejador de rotas a pé).
    validacaoCalcularCaminhada: false,
    // Desligado, o tempo a bordo só é exibido: nenhuma linha é reprovada por ele.
    validacaoConsiderarTempoPermanencia: false,
    // Override manual do limite de permanência na linha; vazio herda linha/unidade/config.
    validacaoTempoMaximoPermanencia: null,
    dialog: false,
    editando: false,
    formValido: false,
    salvando: false,
    loadingCep: false,
    editedItem: { ...PRE_CANDIDATO_VAZIO },
  }),
  mounted() {
    // Depois do mounted do MerusDataTable: o update:options inicial dele já passou
    // e foi barrado por `carregado`, então não há requisição duplicada aqui.
    if (this.ativa) {
      this.ensureLoaded();
    }
  },
  watch: {
    ativa(value) {
      if (value) {
        this.ensureLoaded();
      }
    },
    // Recarregar a tabela (ex.: depois de revalidar) troca os objetos das linhas: a seleção
    // continua nas mesmas linhas, agora com os dados novos. Quem saiu da página fica como está.
    filtroPoligono(valor) {
      this.pageNumber = 1;
      if (valor) this.numberOfRows = this.itemsPerPageLastOption;
      Promise.resolve(this.serverTableLoad()).then(() => {
        if (!valor) return;
        // Soma ao que já estava marcado (inclusive em outras páginas).
        const marcados = new Set(this.preCandidatosSelected.map((l) => l.id_pre_candidato));
        this.preCandidatosSelected = this.preCandidatosSelected.concat(this.preCandidatos.filter((l) => !marcados.has(l.id_pre_candidato)));
        this.$emit('area-aplicada', { selecionados: this.preCandidatos.length, total: this.totalPreCandidatos });
      });
    },
    preCandidatos(linhas) {
      if (!this.preCandidatosSelected.length) return;
      const porChave = new Map((linhas || []).map((l) => [l.id_pre_candidato, l]));
      this.preCandidatosSelected = this.preCandidatosSelected.map((s) => porChave.get(s.id_pre_candidato) || s);
    },
    // O mapa vive na tela pai: ela plota as linhas marcadas nesta tabela.
    preCandidatosSelected: {
      handler(value) {
        this.$emit('update:selecao', Array.isArray(value) ? value : []);
      },
    },
  },
  computed: {
    /** As abas ficam montadas com v-show: só a aba visível exibe o loading, para não sobrepor as outras. */
    loading() {
      return this.ativa && (this.loadingtable ||
        this.loadingCep ||
        this.salvando);
    },
    /**
     * Override manual do tempo máximo de permanência na linha, como o backend espera:
     * inteiro positivo de minutos, ou null para herdar linha / unidade / configurações.
     */
    tempoMaximoPermanenciaPayload() {
      const v = this.validacaoTempoMaximoPermanencia;
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
    },
    /**
     * Mais recente primeiro: a tabela passa de 140k linhas vindas de importação,
     * e o que interessa na tela é o que acabou de entrar.
     */
    defaultSort() {
      return [
        { value: 'id_pre_candidato', options: { text: this.$t('system.labels.decreasing'), value: -1 } }];
    },
    headers() {
      // Sem cod-tabela: `sortable`/`filtrable` precisam vir explícitos
      // (o MerusDataTable não assume default quando os headers não vêm de tabelas_front).
      const col = (text, value) => ({ text, value, sortable: true, filtrable: true, show: true });
      return [
        col(this.$t('preCandidate.headers.id'), "id_pre_candidato"),
        col(this.$t('preCandidate.headers.name'), "nome_completo"),
        col(this.$t('candidate.headers.main.agency'), "agencia_emprego"),
        col(this.$t('address.street'), "rua"),
        col(this.$t('address.number'), "numero"),
        col(this.$t('address.district'), "bairro"),
        col(this.$t('address.city'), "cidade"),
        col(this.$t('address.postalCode'), "cep"),
        col(this.$t('preCandidate.headers.desiredShift'), "turno_desejado"),
        col(this.$t('validationSituation.headers.situation'), "nome_situacao"),
        {
          text: this.$t('area.riskZone'),
          value: "zonas_risco_label",
          sortable: false,
          filtrable: false,
          show: true,
        },
        col(this.$t('preCandidate.headers.ticket'), "id_ticket"),
        {
          text: this.$t('preCandidate.headers.inclusionDate'),
          value: "dt_inclusao_extenso",
          sortable: true,
          filtrable: false,
          show: true,
        },
        {
          text: this.$t('candidate.headers.main.actions'),
          value: "actions",
          sortable: false,
          filtrable: false,
          show: true,
        },
      ];
    },
    headersCsvExport() {
      return this.headers
        .filter((h) => h && h.value && h.value !== "actions")
        .map((h) => ({ text: h.text != null ? h.text : String(h.value), value: h.value }));
    },
    agenciasFiltro() {
      return [
        { id_agencia_emprego: AGENCIA_SEM_VINCULO, cod_agencia: null, nome: this.$t('externalAddress.filters.noAgency') },
        ...(this.agencias || []),
      ];
    },
    validacaoTemErro() {
      return (this.validacaoResultados || []).some((r) => r.erro);
    },
    formTitle() {
      return this.editando ? this.$t('preCandidate.edit') : this.$t('preCandidate.new');
    },
    /** Filtros de coluna + os seletores da barra, no formato aceito por buildRequestParams. */
    requestFilters() {
      const filters = Object.assign({}, this.searchFilters);
      if (this.filtroAgenciaSelected != null && this.filtroAgenciaSelected !== '') {
        filters.id_agencia_emprego = this.filtroAgenciaSelected;
      }
      if (this.filtroSituacaoSelected != null && this.filtroSituacaoSelected !== '') {
        filters.validacao = this.filtroSituacaoSelected;
      }
      if (this.filtroSomenteAvulsos) {
        // -1 = sem ticket, ou seja, cadastrados pela própria tela.
        filters.id_ticket = -1;
      }
      if (this.filtroIds) filters.ids = this.filtroIds;
      if (this.filtroPoligono) filters.poligono = this.filtroPoligono;
      return filters;
    },
  },
  methods: {
    agenciaLabel(agencia) {
      if (!agencia) return '';
      const cod = agencia.cod_agencia != null ? String(agencia.cod_agencia).trim() : '';
      const nome = agencia.nome != null ? String(agencia.nome).trim() : '';
      if (cod && nome) return `${cod} - ${nome}`;
      return cod || nome;
    },
    /**
     * Carrega a página atual. Roda no mounted (quando a aba já abre ativa) e a cada
     * vez que a aba volta a ficar ativa. `carregado` só destrava os eventos que a
     * tabela emite ao montar — ver serverTableLoad().
     */
    ensureLoaded() {
      if (!this.carregado) {
        this.carregado = true;
        if (this.serverSortBy.length === 0 && this.defaultSort.length > 0) {
          const { serverSortBy, serverSortDesc } = initSortFromDefault(this.defaultSort);
          this.serverSortBy = serverSortBy;
          this.serverSortDesc = serverSortDesc;
        }
      }
      const comSelecao = this.prepararSelecaoInicial();
      const carga = this.serverTableLoad();
      if (!comSelecao) return carga;
      return Promise.resolve(carga).then(() => {
        this.preCandidatosSelected = this.preCandidatos.slice();
      });
    },
    onFiltroChange() {
      this.pageNumber = 1;
      this.serverTableLoad();
    },
    serverTableLoad(options) {
      // Ignora os eventos que a tabela emite ao montar, enquanto a aba não foi aberta.
      if (!this.carregado) return Promise.resolve();
      const state = {
        pageNumber: this.pageNumber,
        numberOfRows: this.numberOfRows,
        serverSortBy: this.serverSortBy,
        serverSortDesc: this.serverSortDesc,
        searchFilters: this.requestFilters,
        lastItemsPerPageBeforeTodos: this.lastItemsPerPageBeforeTodos,
        total: this.totalPreCandidatos,
      };
      const { params, page, rowsPerPage, sortByArr, sortDescArr } = buildRequestParams(options, state);
      this.loadingtable = true;
      return (async () => {
        try {
          const response = await apiController.request(apiController.endpoints.getallPrecandidatos, {
            data: params,
            config: { timeout: 120000 },
            silent: true,
          });
          const data = response.data;
          if (data && typeof data.total === 'number' && Array.isArray(data.items)) {
            this.preCandidatos = data.items;
            const updates = computePageState(
              data,
              { page, rowsPerPage, sortByArr, sortDescArr },
              state,
              this.itemsPerPageLastOption
            );
            this.totalPreCandidatos = updates.total;
            this.pageNumber = updates.pageNumber;
            this.numberOfRows = updates.numberOfRows;
            this.lastItemsPerPageBeforeTodos = updates.lastItemsPerPageBeforeTodos;
            this.serverSortBy = updates.serverSortBy;
            this.serverSortDesc = updates.serverSortDesc;
          } else {
            this.preCandidatos = Array.isArray(data) ? data : [];
            this.totalPreCandidatos = this.preCandidatos.length;
          }
          if (data && data.erro) {
            EventBus.$emit('show-alert', { tipo: 'error', mensagem: data.mensagem });
          }
        } catch (e) {
          EventBus.$emit('show-error', { error: e });
          throw e;
        } finally {
          this.loadingtable = false;
        }
      })();
    },
    async loadItemsForExport(onProgress) {
      return fetchAllServerPagesForExport({
        fetchPage: async (params) => {
          const response = await apiController.request(
            apiController.endpoints.getallPrecandidatos,
            { data: params, config: { timeout: 120000 }, silent: true },
          );
          return response && response.data;
        },
        state: {
          total: this.totalPreCandidatos,
          serverSortBy: this.serverSortBy,
          serverSortDesc: this.serverSortDesc,
          searchFilters: this.requestFilters,
        },
        onProgress,
      });
    },
    /**
     * Valida o endereço dos pré-candidatos no mesmo endpoint da validação em lote
     * (modo `precandidatos` do validacandidatosbulk). O backend geocodifica quando
     * lat/lng ainda não foram gravados, aplica a distância a pé e grava
     * `validacao` / `validacao_por_turno`.
     */
    /**
     * Com menos de 30 itens a API valida na própria requisição e devolve o resultado; a partir
     * disso enfileira no worker do scheduler (202) e o resultado chega pelo acompanhamento.
     */
    validaPreCandidatos(lista) {
      const itens = Array.isArray(lista)
        ? lista.filter((p) => p != null && p.id_pre_candidato != null)
        : [];
      if (itens.length === 0) {
        EventBus.$emit('show-alert', {
          tipo: 'warning',
          mensagem: this.$t('preCandidate.validateNoSelection'),
        });
        return;
      }
      this.loadingtable = true;
      const timeoutMs = Math.max(180000, 90000 * Math.ceil(itens.length / 4));
      return (async () => {
        try {
          const response = await apiController.request(
            apiController.endpoints.postvalidacaoprecandidatos,
            {
              data: {
                precandidatos: itens.map((p) => ({
                  id_pre_candidato: p.id_pre_candidato,
                  id_agencia_emprego: p.id_agencia_emprego != null ? p.id_agencia_emprego : null,
                  rua: p.rua,
                  numero: p.numero,
                  bairro: p.bairro,
                  cidade: p.cidade,
                  cep: p.cep,
                  lat: p.lat,
                  lng: p.lng,
                  checkCapacity: this.validacaoCheckCapacity,
                  considerarReservas: this.validacaoConsiderarReservas,
                  tempoMaximoPermanenciaLinha: this.tempoMaximoPermanenciaPayload,
                  considerarTempoPermanencia: this.validacaoConsiderarTempoPermanencia,
                })),
                checkCapacity: this.validacaoCheckCapacity,
                considerarReservas: this.validacaoConsiderarReservas,
                tempoMaximoPermanenciaLinha: this.tempoMaximoPermanenciaPayload,
                considerarTempoPermanencia: this.validacaoConsiderarTempoPermanencia,
                calcularCaminhada: this.validacaoCalcularCaminhada,
              },
              config: { timeout: timeoutMs },
              silent: true,
            }
          );
          // Acima do limite da rota o lote é enfileirado (202): o POST volta na hora e o
          // resultado chega pelo scheduler, então a conexão não fica presa até o timeout.
          const data = await this.consumirRespostaValidacaoBulk(response, itens.length);
          this.validacaoResultados = this.buildValidacaoResultados(itens, data);
          this.validacaoDialog = true;
          await this.serverTableLoad();
        } catch (e) {
          const msg =
            e && e.code === 'ECONNABORTED'
              ? this.$t('candidate.validationRequestTimeout')
              : (e && e.message) || String(e);
          EventBus.$emit('show-alert', { tipo: 'error', mensagem: msg });
        } finally {
          this.encerrarValidacaoScheduler();
          this.loadingtable = false;
        }
      })();
    },
    /**
     * Consulta o processo no scheduler. Fica no componente, e não no mixin, porque o gerador
     * do permissions.manifest.json só varre arquivos .vue: uma chamada a apiController.endpoints
     * escondida num .js faria a permissão do endpoint sumir desta tela.
     */
    buscarProcessoScheduler(idProcesso) {
      return apiController.request(apiController.endpoints.schedulerGetProcessoschedulerbyid, {
        data: { id_processo: idProcesso },
        config: { timeout: 30000 },
        silent: true,
      });
    },
    /**
     * Manda a API gravar o resultado do processo (o worker Python só valida) e devolve os
     * results[] já no formato da resposta síncrona. Fica no componente, e não no mixin, porque
     * o gerador do permissions.manifest.json só varre arquivos .vue.
     */
    aplicarValidacaoScheduler(idProcesso) {
      return apiController.request(apiController.endpoints.aplicaValidacaobulk, {
        data: { id_processo: idProcesso },
        config: { timeout: 300000 },
        silent: true,
      });
    },
    /** Rótulo do código `valido` da validação (mesma escala do CRUD de Candidatos). */
    labelValido(valido) {
      const n = Number(valido);
      const t = (k) => this.$t('addressValidation.' + k);
      if (n === 1) return t('statusAttends');
      if (n === 2) return t('statusAttendsNoSeat');
      if (n === 3) return t('statusAttendsNewStop');
      if (n === 4) return t('statusOutsideUnit');
      if (Number.isFinite(n)) return t('statusDoesNotAttend');
      return t('statusNoShiftValidation');
    },
    /** Endereço fora do raio de todas as unidades: todo turno volta "fora da unidade" e ele é reprovado. */
    foraDaUnidade(cercas) {
      return cercas.length > 0 && cercas.every((f) => Number(f.valido) === 4);
    },
    /** Nenhum turno atendido (atende, sem vaga ou criação de ponto): o endereço foi reprovado. */
    enderecoRejeitado(cercas) {
      return !cercas.some((f) => [1, 2, 3].includes(Number(f.valido)));
    },
    corValido(valido) {
      const n = Number(valido);
      if (COR_VALIDACAO[n]) return COR_VALIDACAO[n];
      return Number.isFinite(n) ? COR_VALIDACAO.other : COR_VALIDACAO.none;
    },
    mapCercaValidacao(f) {
      const dash = '-';
      const linhaReta = f.distance != null && f.distance !== '' ? Math.round(Number(f.distance)) : null;
      const rideMinutes = f.rideTimeMinutes != null && f.rideTimeMinutes !== ''
        ? Math.round(Number(f.rideTimeMinutes))
        : null;
      return {
        turno: f.codShift || f.descriptionShift || (f.idShift != null ? String(f.idShift) : dash),
        linha: f.codLine || f.nameLine || dash,
        cerca: f.codFence || f.nameFence || dash,
        distancia: linhaReta != null ? `${linhaReta} m` : dash,
        // walkDistance = a API traçou o percurso a pé; sem ele o valor é a linha reta.
        distanciaReal: !!f.walkDistance,
        vagas: f.availableSeats != null && f.availableSeats !== '' ? String(f.availableSeats) : dash,
        status: this.labelValido(f.valido),
        cor: this.corValido(f.valido),
        // Tempo a bordo (embarque → unidade). Estourar o limite é só aviso.
        tempoLinha: rideMinutes == null
          ? dash
          : this.$t(f.rideTimeEstimated ? 'candidate.rideTime.estimated' : 'candidate.rideTime.minutes', { minutes: rideMinutes }),
        tempoLinhaExcede: f.exceedsMaxRideTime === true,
        tempoLinhaTooltip: f.exceedsMaxRideTime === true
          ? this.$t('candidate.rideTime.exceeds', { minutes: rideMinutes, max: f.maxRideTimeMinutes })
          : '',
        precisaAjusteTurno: !!f.needsShiftAdjustment,
      };
    },
    enderecoResumo(item) {
      return [
        [item.rua, item.numero].filter(Boolean).join(', '),
        item.bairro,
        item.cidade,
      ].filter((v) => v != null && String(v).trim() !== '').join(' - ');
    },
    /** Monta as linhas do diálogo a partir da resposta de /postvalidacaoprecandidatos. */
    buildValidacaoResultados(itens, data) {
      const results = (data && data.results) || [];
      if (results.length === 0) {
        return [{
          id: itens.length === 1 ? itens[0].id_pre_candidato : '',
          nome: itens.length === 1 ? itens[0].nome_completo : '',
          enderecoResumo: itens.length === 1 ? this.enderecoResumo(itens[0]) : '',
          erro: !!(data && data.erro),
          situacaoLabel: null,
          mensagem: (data && data.mensagem) || this.$t('candidate.validationErrorGeneric'),
          cercas: [],
        }];
      }
      return results.map((r, i) => {
        const item =
          itens.find((p) => String(p.id_pre_candidato) === String(r.id_pre_candidato)) || itens[i] || {};
        const validation = r.validation || {};
        const cercas = (validation.bestFences && validation.bestFences.length
          ? validation.bestFences
          : validation.validatedFences || []);
        const situacao = (this.situacoes || []).find(
          (sit) => Number(sit.id_situacao_validacao) === Number(r.situacao)
        );
        return {
          id: r.id_pre_candidato != null ? r.id_pre_candidato : item.id_pre_candidato,
          nome: item.nome_completo || String(r.id_pre_candidato || ''),
          enderecoResumo: this.enderecoResumo(item),
          erro: !!(r.erro || r.situacao_update_ok === false || this.foraDaUnidade(cercas)),
          rejeitado: this.enderecoRejeitado(cercas),
          situacaoLabel: situacao ? situacao.nome : null,
          mensagem: validation.message || r.erro || this.$t('candidate.validationErrorGeneric'),
          cercas: cercas.map((f) => this.mapCercaValidacao(f)),
        };
      });
    },
    novoPreCandidato() {
      this.editando = false;
      this.editedItem = { ...PRE_CANDIDATO_VAZIO };
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.formPreCandidato) this.$refs.formPreCandidato.resetValidation();
      });
    },
    editarPreCandidato(item) {
      this.editando = true;
      this.editedItem = {
        ...PRE_CANDIDATO_VAZIO,
        id_pre_candidato: item.id_pre_candidato,
        id_ticket: item.id_ticket,
        id_agencia_emprego: item.id_agencia_emprego,
        nome_completo: item.nome_completo,
        cep: item.cep,
        cidade: item.cidade,
        bairro: item.bairro,
        rua: item.rua,
        numero: item.numero,
        turno_desejado: item.turno_desejado,
        validacao: item.validacao,
      };
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.formPreCandidato) this.$refs.formPreCandidato.resetValidation();
      });
    },
    fecharDialog() {
      this.dialog = false;
      this.editando = false;
      this.editedItem = { ...PRE_CANDIDATO_VAZIO };
    },
    /** Preenche o endereço pelo CEP (ViaCEP), como no cadastro de candidatos. */
    consultaCEP() {
      const cep = String(this.editedItem.cep || '').replace(/\D/g, '');
      if (cep.length !== 8) return;

      this.loadingCep = true;
      registrarChamadaApiExterna('VIACEP', 'CEP_LOOKUP', 1, this.token);
      axios.get(`https://viacep.com.br/ws/${cep}/json/`)
        .then((response) => {
          if (!response.data.erro) {
            this.editedItem.rua = response.data.logradouro;
            this.editedItem.cidade = response.data.localidade;
            this.editedItem.bairro = response.data.bairro;
          }
        })
        .catch((e) => {
          EventBus.$emit('show-error', { error: e });
        })
        .finally(() => {
          this.loadingCep = false;
        });
    },
    /**
     * Cadastra (sem ticket) ou altera o pré-candidato. `validacao` em branco cai no
     * default do backend (menor id_situacao_validacao), já que a coluna é NOT NULL.
     */
    async salvarPreCandidato() {
      if (this.$refs.formPreCandidato && !this.$refs.formPreCandidato.validate()) {
        return;
      }
      this.salvando = true;
      try {
        const response = await apiController.request(apiController.endpoints.upsertPrecandidato, {
          data: {
            id_pre_candidato: this.editedItem.id_pre_candidato,
            id_ticket: this.editedItem.id_ticket,
            id_agencia_emprego: this.editedItem.id_agencia_emprego,
            nome_completo: this.editedItem.nome_completo,
            rua: this.editedItem.rua,
            numero: this.editedItem.numero,
            bairro: this.editedItem.bairro,
            cidade: this.editedItem.cidade,
            cep: this.editedItem.cep,
            turno_desejado: this.editedItem.turno_desejado,
            validacao: this.editedItem.validacao,
            allowUpdate: this.editando,
          },
          silent: true,
        });
        this.fecharDialog();
        EventBus.$emit('show-alert', {
          tipo: 'success',
          mensagem: (response.data && response.data.mensagem) || this.$t('system.messages.saveSuccess'),
        });
        this.carregado = true;
        await this.serverTableLoad();
      } catch (e) {
        const mensagem = e && e.response && e.response.data && e.response.data.mensagem;
        if (mensagem) {
          EventBus.$emit('show-alert', { tipo: 'error', mensagem });
        } else {
          EventBus.$emit('show-error', { error: e });
        }
      } finally {
        this.salvando = false;
      }
    },
  },
};
</script>

<style scoped>
.validacao-status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.18);
  margin-right: 6px;
  vertical-align: middle;
}
</style>
